import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '@ibsys/kit';
import { CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { RenessansTrendCallItem } from '@/entities/renessans-dashboards';
import { formatDayPeriod, formatInt } from './utils';

type Props = {
  items: RenessansTrendCallItem[];
};

type TooltipPayloadItem = {
  payload?: RenessansTrendCallItem;
  value?: number;
};

type CallsTooltipProps = {
  active?: boolean;
  payload?: TooltipPayloadItem[];
};

const CallsTooltip: FC<CallsTooltipProps> = ({ active, payload }) => {
  if (!active || !payload || payload.length === 0) return null;
  const point = payload[0]?.payload;
  if (!point) return null;
  return (
    <div className="rounded-md border bg-popover text-popover-foreground px-3 py-2 text-xs shadow">
      <div className="font-medium">{point.period}</div>
      <div>{formatInt(point.count)}</div>
    </div>
  );
};

/**
 * Виджет 1 «Динамика звонков»: линейный график количества звонков по периодам
 * (`day`/`week`/`month`). Отображается под числовым счётчиком KPI.
 */
export const CallsChart: FC<Props> = ({ items }) => {
  const { t } = useTranslation('renessansDashboard');

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('charts.callsTrend')}</CardTitle>
      </CardHeader>
      <CardContent>
        {items.length === 0 && <div className="text-muted-foreground text-sm">{t('noData')}</div>}
        {items.length > 0 && (
          <div className="h-40 w-full">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={items} margin={{ top: 8, right: 12, bottom: 0, left: 0 }}>
                <CartesianGrid stroke="var(--color-border)" strokeDasharray="3 3" />
                <XAxis
                  dataKey="period"
                  stroke="var(--color-muted-foreground)"
                  tick={{ fontSize: 12 }}
                  tickLine={false}
                  tickFormatter={formatDayPeriod}
                />
                <YAxis
                  stroke="var(--color-muted-foreground)"
                  tick={{ fontSize: 12 }}
                  tickLine={false}
                  tickFormatter={(value: number) => formatInt(value)}
                  allowDecimals={false}
                />
                <Tooltip content={<CallsTooltip />} cursor={{ stroke: 'var(--color-muted)' }} />
                <Line
                  type="monotone"
                  dataKey="count"
                  stroke="var(--color-primary)"
                  strokeWidth={2}
                  dot={{ r: 3, fill: 'var(--color-primary)' }}
                  activeDot={{ r: 5 }}
                  isAnimationActive={false}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
